// Event Timeline — one styled timeline, two modes (mode prop):
//
//   mode='sdcard' (SD事件時間軸): reads the device's own sdcard event markers
//     (op:get q=range-events, rides the signaling socket → UNREACHABLE-aware retry).
//     Play = the event's −5s…+10s sdcard clip via device VOD (LAN or WebRTC tunnel).
//
//   mode='cloud' (雲事件時間軸): reads the cloud events store (GET /v3/devices/{id}/events,
//     DynamoDB `event_records`). Play = the event's −19s…+24s window → compose a VOD
//     m3u8 from the presigned S3 clips (see cloudM3u8.ts). No device dependency.
//
// Shared: date picker defaults to TODAY (local tz). Cloud events use cursor-based
// pagination (EVENT_PAGE_LIMIT per page) with infinite scroll — scroll to the bottom
// to pull older rows — plus a "load more" button fallback; list is newest-first.

import { camZone, sharedZone, zoneOffsetUs } from '../../lib/camTz';
import { eventAiLine, type EventAi, type RingDesc } from '../../lib/eventAi';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { CameraTzNote } from '../CameraTzNote';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import {
  clusterOfEvent,
  describeLogbookCommand,
  eventFromLogbookItem,
  eventNoRecording,
  fetchDeviceEvents,
  fetchDeviceLogbook,
  loadEventSnapshot,
  forgetEventSnapshot,
  isPlaybackDetectionEvent,
  type EventItem,
  type LogbookItem,
} from '../../features/events/eventsLib';
import { purgeExpiredSnapshots } from '../../features/events/eventSnapshotDb';
import { pbPlaylistUrl } from '../../lib/recPlayback/hlsSource';
import { composeCloudEventPlaylist, CLOUD_EVENT_PRE_US, CLOUD_EVENT_POST_US, fetchCloudEventClips } from '../../lib/recPlayback/cloudM3u8';
import { clipUrlToMp4Blob, withTimeout, transmuxTsToMp4 } from '../../lib/recPlayback/tsToMp4';
import { fetchCloudClipUrl } from '../../lib/recPlayback/recRest';
import { queryRangeEvents, type PbEvent, type SdcardState } from '../../lib/recPlayback/sdcardQuery';
import { fetchSdWindowTs, sdEventWindow, type SdWindowGet } from '../../lib/recPlayback/sdClips';
import { openRemotePlayback, type RemotePlaybackSession } from '../../lib/recPlayback/webrtcPlayback';
import { makeDataChannelLoaderClass } from '../../lib/recPlayback/dataChannelLoader';
import { recFlag } from '../../lib/recPlayback/flags';
import { useDayList, type DeviceRef, type RecRange } from './useRecPlayback';
import { HlsPlayer, type HlsSource, type HlsLoaderFactory } from './HlsPlayer';
import './recPlayback.css';

const DAY_US = 86_400_000_000;
const HOUR_US = 3_600_000_000;
// Cursor-based pagination page size (like the events feed): fetch ~100 rows per page,
// scroll to the bottom to pull older ones (infinite scroll + a "load more" fallback).
const EVENT_PAGE_LIMIT = 100;
// An event's clip is the −5s…+10s window around the event (event at 0), matching the
// firmware event-window (REC_PRE_RECORD_US=5s / REC_POST_US=10s).
const EVENT_PRE_US = 5_000_000;
const EVENT_POST_US = 10_000_000;
// Days are keyed in the BROWSER's local timezone so the picker + range match the
// locally-formatted event times (formatTimestampUs uses toLocaleString).
const localOffsetUs = () => -new Date().getTimezoneOffset() * 60_000_000;
const dayIndexOf = (us: number, offsetUs: number) => Math.floor((us + offsetUs) / DAY_US);
const dayWindowUs = (dayIndex: number, offsetUs: number) => {
  const fromUs = dayIndex * DAY_US - offsetUs;
  return { fromUs, toUs: fromUs + DAY_US };
};

export type EventTimelineMode = 'sdcard' | 'cloud';
function eventKeyOf(deviceId: string, ev: PbEvent): string {
  return `${deviceId}#${ev.eid || ev.event_ts_us}`;
}

/** Trigger a browser download of a progressive-MP4 blob, named `{deviceId}_{yyyyMMdd}_{HHmmss}.mp4`
 * (same rule as iOS/Android). Schedules objectURL revoke. */
function saveBlobAsMp4(deviceId: string, eventTsUs: number, blob: Blob) {
  const objectUrl = URL.createObjectURL(blob);
  const d = new Date(eventTsUs / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const safeId = deviceId.replace(/[^A-Za-z0-9._-]/g, '_');
  const name = `${safeId}_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.mp4`;
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

function localeOf(language: string): string {
  return language.startsWith('zh') ? 'zh-TW' : language;
}

// dayIndex * DAY_US = 那一天在「顯示時區」的午夜、當成 UTC 看 → 一律用 UTC 取年月日（不受這台電腦的時區影響；offsetUs 留著相容呼叫端）
function formatLocalDate(dayIndex: number, _offsetUs: number, locale: string): string {
  return new Date((dayIndex * DAY_US) / 1000).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDayOption(dayIndex: number, _offsetUs: number): string {
  const d = new Date((dayIndex * DAY_US) / 1000);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatClock(us: number, locale: string, withSeconds = true, timeZone?: string): string {
  return new Date(us / 1000).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined,
    hour12: false,
    timeZone,
  });
}

function rangePct(us: number, range: RecRange): number {
  const span = Math.max(1, range.toUs - range.fromUs);
  return Math.max(0, Math.min(100, ((us - range.fromUs) / span) * 100));
}

function pctStyle(pct: number): CSSProperties {
  return { '--rp-left': `${pct}%` } as CSSProperties;
}

function eventBarStyle(ev: PbEvent, range: RecRange): CSSProperties {
  const startUs = Math.max(range.fromUs, ev.clip_start_us || ev.event_ts_us);
  const endUs = Math.min(range.toUs, ev.clip_end_us || ev.event_ts_us);
  const left = rangePct(startUs, range);
  const width = Math.max(0.25, rangePct(endUs, range) - left);
  return {
    '--rp-left': `${left}%`,
    '--rp-width': `${width}%`,
  } as CSSProperties;
}

function makeHourTicks(range: RecRange, offsetUs: number): number[] {
  const spanHours = Math.max(1, (range.toUs - range.fromUs) / HOUR_US);
  const stepHours = spanHours > 18 ? 4 : spanHours > 8 ? 2 : 1;
  const stepUs = stepHours * HOUR_US;
  const first = Math.ceil((range.fromUs + offsetUs) / stepUs) * stepUs - offsetUs;
  const ticks: number[] = [];
  for (let ts = first; ts <= range.toUs; ts += stepUs) {
    if (ts >= range.fromUs) ticks.push(ts);
  }
  return ticks;
}

type StorageKind = 'cloud' | 'sdcard';

function rowStorageKind(ev: PbEvent): StorageKind {
  // 可播放性/分類只看「這段影片錄在哪」(record_location),與裝置當前設定無關。
  return ev.record_location === 'cloud' ? 'cloud' : 'sdcard';
}

function prettyPayloadLabel(payload: EventItem['payload']): string | null {
  const raw = payload?.detect_label ?? payload?.label;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return raw.replace(/[_-]+/g, ' ').trim();
}

function rowSubtitle(clusterLabel: string, row: SdcardRow): string {
  const parts = [clusterLabel];
  const payloadLabel = row.meta?.label;
  if (payloadLabel && payloadLabel.toLowerCase() !== clusterLabel.toLowerCase()) parts.push(payloadLabel);
  if (row.ev.record_quality) parts.push(row.ev.record_quality.toUpperCase());
  return parts.join(' · ');
}

export interface EventTimelineViewProps {
  devices: DeviceRef[];
  /** @deprecated the view now derives its own single-day range from the date picker. */
  range?: RecRange;
  /** Same-LAN device base URL for sdcard VOD playback. Absent → not directly playable. */
  deviceHlsBase?: (deviceId: string) => string | undefined;
  /** 'sdcard' (default): device markers + device-VOD playback. 'cloud': DDB events
   * (GET /v3/devices/{id}/events) + −19/+24 S3 m3u8 playback. */
  mode?: EventTimelineMode;
}

interface SdcardRow {
  deviceId: string;
  ev: PbEvent;
  meta?: {
    kind?: 'event' | 'command';
    label?: string | null;
    commandLabel?: string | null;
    action?: string | null;
    /** ls-loop ME：AI 電腦的判斷（雲端事件直接帶；SD 事件依 eid 從雲端 logbook 對上）。 */
    ai?: EventAi;
    /** 門鈴沒人接的按鈴說明（有就優先顯示）。 */
    ai_desc?: RingDesc;
  };
}

/** SD 事件列補上 AI 判斷：同一時段的雲端 logbook 依 eid 對（攝影機的 eid 跟雲端事件同一個）。抓不到就不補，不擋列表。 */
async function attachAiByEid(rows: SdcardRow[], fromUs: number, toUs: number): Promise<SdcardRow[]> {
  const byDev = new Map<string, Map<string, { ai?: EventAi; ai_desc?: RingDesc }>>();
  for (const dev of [...new Set(rows.map((r) => r.deviceId))]) {
    const m = new Map<string, { ai?: EventAi; ai_desc?: RingDesc }>();
    try {
      let cursor: string | null = null;
      for (let page = 0; page < 5; page++) {
        const res = await fetchDeviceLogbook(dev, { from: fromUs, to: toUs, cursor, limit: 100 });
        for (const it of res.items) if (it.eid && (it.ai || it.ai_desc)) m.set(it.eid, { ai: it.ai, ai_desc: it.ai_desc });
        cursor = res.nextCursor;
        if (!cursor) break;
      }
    } catch {
      /* 雲端抓不到：SD 列照常顯示，只是沒有 AI 小字 */
    }
    byDev.set(dev, m);
  }
  return rows.map((r) => {
    const hit = byDev.get(r.deviceId)?.get(r.ev.eid);
    return hit ? { ...r, meta: { ...r.meta, ...hit } } : r;
  });
}

/** Map a cloud EventItem (GET /v3/devices/{id}/events) into the PbEvent row shape the
 * list renders. eid ← correlation_id (or event_id as a stable fallback key); the clip
 * window is informational only (cloud playback derives its own −19/+24 span). */
function cloudEventToPbEvent(ev: EventItem): PbEvent {
  const wire = ev as EventItem & {
    eid?: string;
    event_ts_us?: number;
    record_location?: string;
    record_mode?: string;
    record_quality?: string;
  };
  const eventTsUs = typeof wire.event_ts_us === 'number' && wire.event_ts_us > 0 ? wire.event_ts_us : ev.event_ts;
  return {
    eid: wire.eid || ev.correlation_id || ev.event_id,
    event_ts_us: eventTsUs,
    clip_start_us: eventTsUs - CLOUD_EVENT_PRE_US,
    clip_end_us: eventTsUs + CLOUD_EVENT_POST_US,
    event_id_hex: ev.event_id,
    // 縮圖有無取自後端真欄位 has_snapshot(EventItem/00 §19);事件從不內嵌 snapshot_url,
    // 舊寫法 Boolean(snapshot_url) 恆 false → 縮圖永不抓。true 時才呼叫 per-event snapshot 端點。
    has_snapshot: Boolean(ev.has_snapshot),
    // 缺欄位 = 不知道錄在哪 → 不當雲端（以前補 'cloud'，2505 沒帶欄位的事件混進雲端回放、點了也播不出來）
    record_location: wire.record_location ?? '',
    record_mode: wire.record_mode,
    record_quality: wire.record_quality,
  };
}

function commandLogbookToPbEvent(item: LogbookItem): PbEvent {
  const tsUs = item.ts_us || Date.now() * 1000;
  return {
    eid: item.request_id || `${item.action ?? 'command'}-${tsUs}`,
    event_ts_us: tsUs,
    clip_start_us: tsUs,
    clip_end_us: tsUs,
    event_id_hex: '',
    has_snapshot: false,
    record_location: 'cloud',
    record_mode: 'command',
  };
}

function isCommandRow(row: SdcardRow): boolean {
  return row.meta?.kind === 'command';
}

function logbookItemFromEvent(ev: EventItem): LogbookItem {
  return {
    kind: 'event',
    ts_us: ev.event_ts,
    event_id: ev.event_id,
    event_type: ev.event_type,
    correlation_id: ev.correlation_id,
    payload: ev.payload,
  };
}

/** Map one page of cloud logbook items into SdcardRow[] (shared by first-page + load-more).
 * Command rows become audit rows; event rows map via cloudEventToPbEvent, DROPPING any whose
 * record_location !== 'cloud' (this timeline is cloud-recording only — see the inline note). */
export function cloudLogbookRows(deviceId: string, items: LogbookItem[]): SdcardRow[] {
  const out: SdcardRow[] = [];
  for (const item of items) {
    if (item.kind === 'command') {
      if (item.action === 'command.get') continue; // 唯讀查詢沒動到裝置，不列（伺服器 logbook 也濾，這裡擋舊版 API）
      out.push({
        deviceId,
        ev: commandLogbookToPbEvent(item),
        meta: {
          kind: 'command',
          commandLabel: describeLogbookCommand(item),
          action: item.action ?? null,
        },
      });
      continue;
    }
    const ev = eventFromLogbookItem(item);
    if (!ev) continue;
    const pbev = cloudEventToPbEvent(ev);
    // 雲事件時間軸是給「雲端錄影回放」用的。事件庫(logbook)記錄所有事件,含錄在 SD 卡的;
    // 那些不屬於這裡 —— 依事件自身 record_location 過濾掉非 cloud 的事件列
    // (record_location 遺漏才 fallback cloud,見 cloudEventToPbEvent)。
    if (pbev.record_location !== 'cloud') continue;
    out.push({
      deviceId,
      ev: pbev,
      meta: { kind: 'event', label: prettyPayloadLabel(ev.payload), ai: ev.ai, ai_desc: ev.ai_desc },
    });
  }
  return out;
}

/** Per-device cursor-pagination state for the cloud events feed. */
interface CloudFeed {
  /** Next-page cursor (null once the server reports no further page). */
  cursor: string | null;
  /** No further pages for the current window. */
  done: boolean;
  /** Rows accumulated across the pages loaded so far. */
  rows: SdcardRow[];
}

/** Read events for the window across the given devices (newest-first).
 *
 * mode='cloud': cursor-based pagination like the events feed (spec 17 §4) — page 1 on
 *   (device|window|reload) change, then loadMore() pulls the next page for every
 *   not-done device (infinite scroll drives it). Rows are accumulated, deduped by
 *   eventKeyOf, and sorted newest-first. A generation counter drops pages that land
 *   after the window/device set changed.
 *
 * mode='sdcard': single op:get per device that rides the signaling socket; right after
 *   a hard reload it may not be connected yet and returns UNREACHABLE — so on an
 *   empty+unreachable result we auto-retry a few times (unchanged behavior). */
function useSdcardEvents(devices: DeviceRef[], range: RecRange, mode: EventTimelineMode) {
  const deviceKey = devices.map((d) => d.id).join(',');
  const [rows, setRows] = useState<SdcardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Cloud pagination: per-device feed kept in a ref (rows come from feeds, not state).
  const feedsRef = useRef<Record<string, CloudFeed>>({});
  // Generation guard: a window/device change invalidates in-flight page loads.
  const genRef = useRef(0);

  // Merge every device's accumulated rows, dedup by eventKeyOf, sort newest-first.
  const mergedFromFeeds = useCallback((): SdcardRow[] => {
    const seen = new Set<string>();
    const merged: SdcardRow[] = [];
    for (const feed of Object.values(feedsRef.current)) {
      for (const row of feed.rows) {
        const k = eventKeyOf(row.deviceId, row.ev);
        if (seen.has(k)) continue;
        seen.add(k);
        merged.push(row);
      }
    }
    merged.sort((a, b) => b.ev.event_ts_us - a.ev.event_ts_us); // newest first
    return merged;
  }, []);

  // Load ONE cloud page for a device. cursor=null → first page (replaces rows); a
  // non-null cursor appends. 404 on /logbook → same fallback to /events as before.
  const loadCloudPage = useCallback(
    async (deviceId: string, cursor: string | null, gen: number) => {
      let items: LogbookItem[];
      let nextCursor: string | null;
      try {
        const res = await fetchDeviceLogbook(deviceId, { from: range.fromUs, to: range.toUs, cursor, limit: EVENT_PAGE_LIMIT });
        items = res.items;
        nextCursor = res.nextCursor;
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 404) throw e;
        const fallback = await fetchDeviceEvents(deviceId, { from: range.fromUs, to: range.toUs, cursor, limit: EVENT_PAGE_LIMIT });
        items = fallback.items.map(logbookItemFromEvent);
        nextCursor = fallback.nextCursor;
      }
      if (gen !== genRef.current) return; // window/device changed while in flight → drop
      const mapped = cloudLogbookRows(deviceId, items);
      const prev = feedsRef.current[deviceId];
      feedsRef.current[deviceId] = {
        cursor: nextCursor,
        // Use the RAW page length, not the mapped one: a page that is all sdcard rows
        // (filtered out) can still have a next cursor worth following.
        done: nextCursor === null || items.length === 0,
        rows: cursor === null ? mapped : (prev?.rows ?? []).concat(mapped),
      };
    },
    [range.fromUs, range.toUs]
  );

  useEffect(() => {
    const gen = ++genRef.current;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (mode === 'cloud') {
      feedsRef.current = {};
      setRows([]);
      setHasMore(false);
      setLoadingMore(false);
      setLoading(true);
      setUnreachable(false);
      void (async () => {
        let anyErr = false;
        await Promise.all(
          devices.map(async (dev) => {
            try {
              await loadCloudPage(dev.id, null, gen);
            } catch {
              anyErr = true;
            }
          })
        );
        if (!alive || gen !== genRef.current) return;
        const merged = mergedFromFeeds();
        setRows(merged);
        setHasMore(Object.values(feedsRef.current).some((f) => !f.done));
        setUnreachable(anyErr && merged.length === 0);
        setLoading(false);
      })();
      return () => {
        alive = false;
      };
    }

    // sdcard: single op:get per device + UNREACHABLE-aware retry loop (unchanged).
    let attempt = 0;
    setRows([]);
    setHasMore(false);
    setLoadingMore(false);
    setLoading(true);
    setUnreachable(false);
    const run = async () => {
      const merged: SdcardRow[] = [];
      let anyUnreachable = false;
      for (const dev of devices) {
        try {
          const res = await queryRangeEvents(dev.id, range.fromUs, range.toUs);
          const st: SdcardState = res.state;
          if (st === 'UNREACHABLE') anyUnreachable = true;
          for (const ev of res.items ?? []) merged.push({ deviceId: dev.id, ev });
        } catch {
          anyUnreachable = true;
        }
      }
      if (!alive) return;
      if (merged.length === 0 && anyUnreachable && attempt < 4) {
        attempt += 1; // socket likely still connecting — retry shortly
        timer = setTimeout(run, 1500);
        return;
      }
      merged.sort((a, b) => b.ev.event_ts_us - a.ev.event_ts_us); // newest first
      setRows(merged);
      setUnreachable(anyUnreachable && merged.length === 0);
      // ls-loop ME：SD 列先顯示，再背景補 AI 判斷（依 eid 對雲端 logbook），不拖慢列表
      if (merged.length > 0) {
        void attachAiByEid(merged, range.fromUs, range.toUs).then((withAi) => {
          if (alive) setRows(withAi);
        });
      }
      setLoading(false);
    };
    void run();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, range.fromUs, range.toUs, reloadKey, mode]);

  const loadMore = useCallback(() => {
    if (mode !== 'cloud') return; // sdcard: single fetch, no pagination
    const gen = genRef.current;
    const targets = devices.filter((d) => {
      const f = feedsRef.current[d.id];
      return f && !f.done && f.cursor;
    });
    if (targets.length === 0) return;
    setLoadingMore(true);
    void (async () => {
      await Promise.all(
        targets.map(async (d) => {
          try {
            await loadCloudPage(d.id, feedsRef.current[d.id]?.cursor ?? null, gen);
          } catch {
            // Persistent error on this device → mark done so we don't hot-loop the sentinel.
            const f = feedsRef.current[d.id];
            if (f) feedsRef.current[d.id] = { ...f, done: true };
          }
        })
      );
      if (gen !== genRef.current) return; // superseded by a window/device change
      setRows(mergedFromFeeds());
      setHasMore(Object.values(feedsRef.current).some((f) => !f.done));
      setLoadingMore(false);
    })();
  }, [devices, mode, loadCloudPage, mergedFromFeeds]);

  return {
    rows,
    loading,
    loadingMore,
    unreachable,
    hasMore,
    loadMore,
    reload: () => setReloadKey((n) => n + 1),
  };
}

// ── Per-event snapshot thumbnail (回放事件時間軸 slot) ─────────────────────────
// 交付「跟 clip 影片一樣」:has_snapshot 為 true 時呼叫 per-event snapshot 端點拿一枚 URL,
// 直接 `<img src={url}>` 顯示。web client 拿到的是**同源 proxy** URL(/v3/recordings/download,
// restful 從 snapshot 桶串流)—— 瀏覽器 CSP 禁止直連跨來源 S3,故走同源,與 `<video src>` 對稱
// (img-src 已放行 v3-api-dev)。不 fetch→blob(那需要 CORS 且 blob: 也被 img-src 擋)。
// 以 eid memoize URL(短效簽名,session 內重用即可);載入中 / 無圖 / 載入失敗 → 退回 rp-thumb-glyph。
function RpEventThumb({ deviceId, ev }: { deviceId: string; ev: PbEvent }) {
  const eid = ev.eid;
  const hasSnapshot = ev.has_snapshot;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!eid || !hasSnapshot) return;
    const ctrl = new AbortController();
    let alive = true;
    void loadEventSnapshot(deviceId, eid, ctrl.signal).then((u) => {
      if (alive && u) setUrl(u);
    });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [deviceId, eid, hasSnapshot]);
  // 有圖疊 <img> 於 slot 上;URL 過期 / 載入失敗 → onError 清掉退回 glyph(setUrl(null) 不在
  // effect deps → 不重抓、無閃爍迴圈)。
  return url ? (
    <img className="rp-event-thumb-img" src={url} alt="" onError={() => { if (eid) forgetEventSnapshot(eid); setUrl(null); }} />
  ) : (
    <span className="rp-thumb-glyph" />
  );
}

export function EventTimelineView({ devices, deviceHlsBase, mode = 'sdcard' }: EventTimelineViewProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = useMemo(() => localeOf(i18n.language), [i18n.language]);

  // 本地縮圖 90 天自刪:開頁清一次過期的 IndexedDB 快取(best-effort,無 IndexedDB 時 no-op)。
  useEffect(() => {
    void purgeExpiredSnapshots();
  }, []);

  // ── Date picker (local tz) ─────────────────────────────────────────────
  const primaryDeviceId = devices[0]?.id ?? '';
  // 顯示時區：看的攝影機都在同一個時區 → 用攝影機時區（對齊 Android）；混著不同時區或還不知道 → 這台電腦的時區
  const viewZone = sharedZone(devices.map((d) => d.id));
  const dayList = useDayList(primaryDeviceId);
  const offsetUs = useMemo(() => (viewZone ? zoneOffsetUs(viewZone) : localOffsetUs()), [viewZone]);
  const todayLocal = useMemo(() => dayIndexOf(Date.now() * 1000, offsetUs), [offsetUs]);
  const [dayIndex, setDayIndex] = useState<number>(() => dayIndexOf(Date.now() * 1000, offsetUs));
  // 時區晚一點才知道（裝置資料到了）→ 時差變了：還停在「今天」就重新對齊到攝影機時區的今天；使用者挑過別天就不動
  const prevTodayRef = useRef(todayLocal);
  useEffect(() => {
    setDayIndex((cur) => (cur === prevTodayRef.current ? todayLocal : cur));
    prevTodayRef.current = todayLocal;
  }, [todayLocal]);
  const isToday = dayIndex === todayLocal;
  const range = useMemo<RecRange>(() => {
    // Today: from local midnight up to "now" (open at both ends of a full day so cursor
    // pagination can walk the whole day). Past days: the full local-day window.
    if (isToday) {
      return { fromUs: dayWindowUs(dayIndex, offsetUs).fromUs, toUs: Date.now() * 1000 };
    }
    return dayWindowUs(dayIndex, offsetUs);
  }, [dayIndex, isToday, offsetUs]);
  const dayOptions = useMemo(() => {
    const set = new Set<number>();
    for (const d of dayList.days) {
      const utcFrom = d.day_index * DAY_US;
      set.add(dayIndexOf(utcFrom, offsetUs));
      // Clamp the UTC day's end to "now": today's still-incomplete UTC day must not surface a
      // FUTURE local-day option (UTC 7/2 tail → UTC+8 7/3 before that local day has begun).
      set.add(dayIndexOf(Math.min(utcFrom + DAY_US - 1, Date.now() * 1000), offsetUs));
    }
    set.add(todayLocal);
    return [...set].sort((a, b) => b - a);
  }, [dayList.days, offsetUs, todayLocal]);

  const { rows, loading, loadingMore, unreachable, hasMore, loadMore, reload } = useSdcardEvents(devices, range, mode);
  const [source, setSource] = useState<HlsSource | null>(null);
  const [loaderFactory, setLoaderFactory] = useState<HlsLoaderFactory | undefined>(undefined);
  const [playError, setPlayError] = useState<string | null>(null);
  // 事件列下載中的那筆(轉圈圈用);null = 沒有下載進行中。
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  // True while an event's media is loading (between the click and the first source):
  // shows a "loading" spinner instead of a bare black player, so a switch/slow load
  // is not a mystery blank screen.
  const [playLoading, setPlayLoading] = useState(false);
  const [activeEventKey, setActiveEventKey] = useState<string | null>(null);
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  // Default the storage sub-filter to THIS timeline's own kind: the cloud timeline's rows are
  // already cloud-only (filtered at fetch) and the SD timeline's rows are all sdcard markers,
  // so a fixed 'cloud' default left the SD timeline showing nothing until an unreliable
  // auto-switch fired. Match the mode so each timeline shows its events immediately.
  const [storageFilter, setStorageFilter] = useState<StorageKind>(mode === 'cloud' ? 'cloud' : 'sdcard');
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const eventBodyRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const revealActiveRef = useRef(false);
  const availableStorage = useMemo(() => {
    const kinds = new Set<StorageKind>();
    for (const row of rows) kinds.add(rowStorageKind(row.ev));
    return { cloud: kinds.has('cloud'), sdcard: kinds.has('sdcard') };
  }, [rows]);
  useEffect(() => {
    if (storageFilter === 'cloud' && !availableStorage.cloud && availableStorage.sdcard) setStorageFilter('sdcard');
    if (storageFilter === 'sdcard' && !availableStorage.sdcard && availableStorage.cloud) setStorageFilter('cloud');
  }, [availableStorage.cloud, availableStorage.sdcard, storageFilter]);
  const visibleRows = useMemo(
    // spec 17 §4.1：此處同源濾除 → 清單、計數（visibleRows.length）、時間軸 marker（timelineRows 由此衍生）
    // 三處一致。兩道規則:①只收攝影機偵測 cluster（排除 0x80C0 等操作/系統事件;PbEvent 的 hex id 在
    // event_id_hex）②排除無錄影事件（record_mode==none）。command 列無 hex id 且 record_mode 為 'command'
    // → 兩道都放行,不受影響。
    () =>
      rows.filter(
        (row) =>
          rowStorageKind(row.ev) === storageFilter &&
          isPlaybackDetectionEvent({ event_id: row.ev.event_id_hex ?? '', event_type: '' }) &&
          !eventNoRecording(row.ev)
      ),
    [rows, storageFilter]
  );
  const timelineRows = useMemo(
    () => visibleRows.filter((row) => !isCommandRow(row)).sort((a, b) => a.ev.event_ts_us - b.ev.event_ts_us),
    [visibleRows]
  );
  const hourTicks = useMemo(() => makeHourTicks(range, offsetUs), [offsetUs, range]);
  const displayPlayheadUs = playheadUs ?? visibleRows[0]?.ev.event_ts_us ?? range.toUs;
  const displayPlayheadPct = rangePct(displayPlayheadUs, range);
  useEffect(() => {
    if (!activeEventKey) return;
    if (!visibleRows.some((row) => eventKeyOf(row.deviceId, row.ev) === activeEventKey)) {
      setActiveEventKey(null);
      setPlayheadUs(null);
    }
  }, [activeEventKey, visibleRows]);
  useEffect(() => {
    if (!activeEventKey || !revealActiveRef.current) return;
    revealActiveRef.current = false;
    const row = rowRefs.current.get(activeEventKey);
    if (!row) return;
    const container = eventBodyRef.current;
    if (container && container.scrollHeight > container.clientHeight + 1) {
      const rowRect = row.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetTop = container.scrollTop + (rowRect.top - containerRect.top) - container.clientHeight * 0.35;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      return;
    }
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeEventKey]);

  // Infinite scroll: pull the next cloud page when the bottom sentinel comes into view
  // (mirrors features/events/EventTimeline). Observe only while more pages remain.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) loadMore();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  // Remote sdcard playback rides a WebRTC data-channel tunnel to the device (same path as
  // the continuous view). One session, reused across plays; closed on unmount.
  const remoteEnabled = recFlag('REC_FEAT_PLAYBACK_REMOTE');
  const sessionRef = useRef<RemotePlaybackSession | null>(null);
  const sessionDeviceRef = useRef<string | null>(null);
  // Cloud playback builds an in-memory HLS session over app-local cached fMP4 clips.
  // Release the previous session on the next play and on unmount (cache bytes stay
  // bounded in CacheStorage/memory; the session only holds current segment views).
  const cloudReleaseRef = useRef<(() => void) | null>(null);
  const cloudFallbackRef = useRef<string | null>(null);
  // Re-click guard: loadSeqRef stamps each playRow invocation so a stale async
  // load (which resolves after a newer click) cannot clobber the live session;
  // loadAbortRef cancels the superseded in-flight request.
  const loadSeqRef = useRef(0);
  const loadAbortRef = useRef<AbortController | null>(null);
  const releaseCloudSession = useCallback(() => {
    cloudReleaseRef.current?.();
    cloudReleaseRef.current = null;
    cloudFallbackRef.current = null;
  }, []);
  useEffect(() => () => {
    loadAbortRef.current?.abort();
    sessionRef.current?.stop();
    sessionRef.current = null;
    releaseCloudSession();
  }, [releaseCloudSession]);

  const playRow = useCallback(
    async (deviceId: string, ev: PbEvent) => {
      // Re-click guard: supersede any in-flight load. Bump the sequence and abort
      // the previous request so a rapid second click can't leave two loads racing
      // to set the source / release each other's session (the "unpredictable" state).
      const seq = ++loadSeqRef.current;
      loadAbortRef.current?.abort();
      const ac = new AbortController();
      loadAbortRef.current = ac;
      revealActiveRef.current = true;
      setActiveEventKey(eventKeyOf(deviceId, ev));
      setPlayheadUs(ev.event_ts_us);
      setPlayError(null);
      // Switching events: clear the player immediately so the previous event stops and
      // does not keep playing while the new one loads (cloud load fetches clips, ~1–2s).
      // source=null unmounts HlsPlayer (tearing down the old session) → placeholder →
      // the new source remounts it once ready.
      setSource(null);
      setPlayLoading(true);
      if (mode === 'cloud') {
        // Cloud playback: play the event's [−5s,+10s] clips (fetched from the wider
        // worst-case window) stitched into one VOD m3u8. See composeCloudEventPlaylist.
        try {
          const pl = await composeCloudEventPlaylist(deviceId, ev.event_ts_us, ac.signal);
          if (seq !== loadSeqRef.current) { pl?.release(); return; } // superseded by a newer click
          if (!pl) { setPlayLoading(false); setPlayError(t('recPlayback.playFailed')); return; }
          releaseCloudSession();
          cloudReleaseRef.current = pl.release;
          cloudFallbackRef.current = pl.fallbackUrl ?? null;
          setLoaderFactory(undefined);
          setSource({ kind: 'device-vod', m3u8Url: pl.m3u8Url });
          setPlayLoading(false);
        } catch {
          if (ac.signal.aborted || seq !== loadSeqRef.current) return; // superseded/aborted: stay silent
          setPlayLoading(false);
          setPlayError(t('recPlayback.playFailed'));
        }
        return;
      }
      // Play the event's −5s…+10s clip (event at 0), not the zero-width marker point.
      releaseCloudSession();
      const startUs = ev.event_ts_us - EVENT_PRE_US;
      const endUs = ev.event_ts_us + EVENT_POST_US;
      const base = deviceHlsBase?.(deviceId);
      if (base) {
        setLoaderFactory(undefined);
        setSource({ kind: 'device-vod', m3u8Url: pbPlaylistUrl(base, startUs, endUs) });
        setPlayLoading(false);
        return;
      }
      if (!remoteEnabled) {
        setPlayLoading(false);
        setPlayError(t('recPlayback.sdcardLanOnly'));
        return;
      }
      // REMOTE: open/reuse a tunnel session to THIS device; hls.js fetches fragments
      // through the data-channel loader against a tunnel-relative /pb.m3u8 URL.
      try {
        if (!sessionRef.current || sessionDeviceRef.current !== deviceId) {
          sessionRef.current?.stop();
          sessionRef.current = await openRemotePlayback(deviceId);
          sessionDeviceRef.current = deviceId;
        }
        if (seq !== loadSeqRef.current) return; // superseded by a newer click
        const tunnel = sessionRef.current.tunnel;
        const lf: HlsLoaderFactory = () => makeDataChannelLoaderClass({ tunnel, onRecycle404: reload });
        setLoaderFactory(() => lf);
        setSource({ kind: 'device-vod', m3u8Url: pbPlaylistUrl('', startUs, endUs) });
        setPlayLoading(false);
      } catch {
        if (seq !== loadSeqRef.current) return; // superseded
        setPlayLoading(false);
        setPlayError(t('recPlayback.deviceUnreachable'));
      }
    },
    [mode, deviceHlsBase, remoteEnabled, releaseCloudSession, reload, t]
  );

  const onPlayerError = useCallback(
    (fatal: boolean) => {
      if (!fatal) return;
      const fallbackUrl = cloudFallbackRef.current;
      if (fallbackUrl) {
        setLoaderFactory(undefined);
        setSource({ kind: 'cloud-clip', url: fallbackUrl });
        return;
      }
      setPlayError(t('recPlayback.playFailed'));
    },
    [t]
  );

  // 下載雲端事件:取事件實際片段(fetchCloudEventClips,與播放同源)→ 涵蓋事件那一段 → transmux 成
  // progressive MP4 → 瀏覽器存檔。雲端 clip 容器是 MPEG-TS,直接存裸 bytes 是「假 mp4」,故一律 transmux。
  const downloadEvent = useCallback(
    async (deviceId: string, ev: PbEvent) => {
      const key = eventKeyOf(deviceId, ev);
      if (downloadingKey) return;
      setDownloadingKey(key);
      try {
        const clips = await fetchCloudEventClips(deviceId, ev.event_ts_us);
        const primary =
          clips.find((c) => c.clip_start_ts <= ev.event_ts_us && ev.event_ts_us <= c.clip_end_ts) ?? clips[0];
        if (!primary) {
          setPlayError(t('recPlayback.downloadNoClip', { defaultValue: '這個事件沒有可下載的雲端片段。' }));
          return;
        }
        const blob = await withTimeout(
          clipUrlToMp4Blob(primary.url, () => fetchCloudClipUrl(primary.clip_id)),
          15_000,
        );
        saveBlobAsMp4(deviceId, ev.event_ts_us, blob);
      } catch (e) {
        console.error('[downloadEvent] failed', e);
        setPlayError(t('recPlayback.downloadFailed', { defaultValue: '下載失敗，請稍後再試。' }));
      } finally {
        setDownloadingKey(null);
      }
    },
    [downloadingKey, t]
  );

  // 下載 SD 卡事件（spec 17 §3.4 SD 腿；業主 2026-09-09 裁決，解除 APP#105 F3「SD 模式不畫下載鈕」）：
  // 存該事件 −5s…+10s 窗（與點播同窗），走 device VOD tunnel（非 S3）——/pb.m3u8 → 逐段拉 /seg/*.ts
  // 接續成一份 TS（fetchSdWindowTs：404 段跳過、其他錯誤才算失敗）→ 同一條 transmux 腿出 MP4 → 存檔。
  // 整段全 404 → 「此時段的 SD 卡片段已被覆蓋」提示、不算失敗。LAN 直連為次選。
  const downloadSdEvent = useCallback(
    async (deviceId: string, ev: PbEvent) => {
      const key = eventKeyOf(deviceId, ev);
      if (downloadingKey) return;
      setDownloadingKey(key);
      let tempSession: RemotePlaybackSession | null = null;
      try {
        const win = sdEventWindow(ev.event_ts_us);
        const base = deviceHlsBase?.(deviceId);
        // 取得「抓 URL」的方法:LAN 直接 fetch;否則走 WebRTC tunnel requestUrl。
        let get: SdWindowGet;
        if (base) {
          const root = base.replace(/\/$/, '');
          get = async (url) => {
            const full = url.includes('://') ? url : `${root}${url.startsWith('/') ? url : `/${url}`}`;
            const r = await fetch(full);
            if (r.status === 404) return { status: 'not_found' };
            if (!r.ok) return { status: 'error' };
            if (full.includes('.m3u8')) return { status: 'ok', text: await r.text() };
            return { status: 'ok', bytes: new Uint8Array(await r.arrayBuffer()) };
          };
        } else {
          if (!remoteEnabled) {
            setPlayError(t('recPlayback.sdcardLanOnly'));
            return;
          }
          let session: RemotePlaybackSession;
          if (sessionRef.current && sessionDeviceRef.current === deviceId) session = sessionRef.current;
          else { tempSession = await openRemotePlayback(deviceId); session = tempSession; }
          get = (url) => session.tunnel.requestUrl(url);
        }
        const { ts, written } = await fetchSdWindowTs(get, win.startUs, win.endUs);
        if (written === 0) {
          setPlayError(t('playback.sdOverwritten'));
          return;
        }
        const blob = await transmuxTsToMp4(ts);
        saveBlobAsMp4(deviceId, ev.event_ts_us, blob);
      } catch (e) {
        console.error('[downloadSdEvent] failed', e);
        setPlayError(t('recPlayback.downloadFailed', { defaultValue: '下載失敗，請稍後再試。' }));
      } finally {
        tempSession?.stop();
        setDownloadingKey(null);
      }
    },
    [downloadingKey, deviceHlsBase, remoteEnabled, t]
  );

  return (
    <div className="rp-view rp-view--event-browser">
      <div className="rp-stage">
        <header className="rp-playback-topbar">
          <button
            type="button"
            className="rp-icon-button rp-back"
            onClick={() => navigate(-1)}
            aria-label={t('recPlayback.back')}
            title={t('recPlayback.back')}
          >
            ‹
          </button>
          <h1 className="rp-title">
            {mode === 'cloud'
              ? t('recPlayback.cloudEventTitle', { defaultValue: '雲端回放' })
              : t('recPlayback.sdEventTitle', { defaultValue: 'SD事件時間軸' })}
          </h1>
          <CameraTzNote deviceId={primaryDeviceId} />
          {/* 下載改為每列事件右側的下載鈕(對齊 iOS/Android)——移除原本那顆寫死 disabled 的
              「下載」分頁佔位鈕(從未實作、只會讓人以為壞掉)。 */}
          <div className="rp-mode-tabs" role="tablist" aria-label={t('recPlayback.eventListTitle')}>
            <button type="button" className="rp-mode-tab is-active" role="tab" aria-selected="true">
              {t('recPlayback.eventListTitle')}
            </button>
          </div>
          {/* ls-loop MV.2：跨攝影機查找（AI 判斷 / 類型 / 時段篩選） */}
          {mode === 'cloud' && (
            <button type="button" className="rp-mode-tab" onClick={() => navigate('/events')} data-testid="event-center-link">
              {t('eventCenter.link')} →
            </button>
          )}
          <button
            type="button"
            className="rp-icon-button"
            onClick={reload}
            aria-label={t('common.retry')}
            title={t('common.retry')}
          >
            ↻
          </button>
        </header>

        <div className="rp-player">
          {source ? (
            <HlsPlayer source={source} loaderFactory={loaderFactory} onError={onPlayerError} />
          ) : playLoading ? (
            <div className="rp-player-empty rp-player-loading" role="status">
              {t('recPlayback.loading')}
            </div>
          ) : (
            <div className="rp-player-empty" aria-hidden />
          )}
        </div>
        {playError && <p className="rp-error" role="alert">{playError}</p>}

        <section className="rp-timeline-panel" aria-label={t('recPlayback.day')}>
          <div className="rp-datebar">
            <button
              type="button"
              className="rp-icon-button"
              onClick={() => setDayIndex((d) => d - 1)}
              aria-label={t('recPlayback.previousDay')}
              title={t('recPlayback.previousDay')}
            >
              ‹
            </button>
            <label className="rp-date-chip">
              <span>{formatLocalDate(dayIndex, offsetUs, locale)}</span>
              <select value={dayIndex} onChange={(e) => setDayIndex(Number(e.target.value))} aria-label={t('recPlayback.day')}>
                {dayOptions.map((di) => (
                  <option key={di} value={di}>
                    {formatDayOption(di, offsetUs)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="rp-icon-button"
              onClick={() => setDayIndex((d) => Math.min(todayLocal, d + 1))}
              disabled={dayIndex >= todayLocal}
              aria-label={t('recPlayback.nextDay')}
              title={t('recPlayback.nextDay')}
            >
              ›
            </button>
            <div className="rp-source-toggle" role="group" aria-label={t('recPlayback.storageSource')}>
              <button
                type="button"
                className={`rp-source-option ${storageFilter === 'cloud' ? 'is-active' : ''}`}
                disabled={!availableStorage.cloud}
                onClick={() => setStorageFilter('cloud')}
                aria-pressed={storageFilter === 'cloud'}
                aria-label={t('recPlayback.labelCloud')}
                title={t('recPlayback.labelCloud')}
              >
                ☁
              </button>
              <button
                type="button"
                className={`rp-source-option ${storageFilter === 'sdcard' ? 'is-active' : ''}`}
                disabled={!availableStorage.sdcard}
                onClick={() => setStorageFilter('sdcard')}
                aria-pressed={storageFilter === 'sdcard'}
                aria-label={t('recPlayback.labelSdcard')}
                title={t('recPlayback.labelSdcard')}
              >
                ▣
              </button>
            </div>
          </div>
          <div className="rp-time-ruler" aria-hidden>
            {hourTicks.map((ts) => (
              <span key={ts} className="rp-hour-tick" style={pctStyle(rangePct(ts, range))}>
                {formatClock(ts, locale, false, viewZone)}
              </span>
            ))}
          </div>
          <div className="rp-event-band" role="group" aria-label={t('recPlayback.eventListTitle')}>
            {timelineRows.map(({ deviceId, ev }) => {
              const key = eventKeyOf(deviceId, ev);
              const storage = rowStorageKind(ev);
              const cluster = clusterOfEvent({ event_id: ev.event_id_hex ?? '', event_type: '' });
              return (
                <button
                  type="button"
                  key={`marker-${key}`}
                  className={`rp-event-marker rp-event-marker--${storage} ${key === activeEventKey ? 'is-active' : ''}`}
                  style={eventBarStyle(ev, range)}
                  onClick={() => playRow(deviceId, ev)}
                  aria-label={`${cluster.label} ${formatTimestampUs(ev.event_ts_us)}`}
                  title={formatClock(ev.event_ts_us, locale, true, camZone(deviceId) ?? viewZone)}
                />
              );
            })}
            <span className="rp-playhead-label" style={pctStyle(displayPlayheadPct)}>
              {formatClock(displayPlayheadUs, locale, true, viewZone)}
            </span>
            <span className="rp-playhead" style={pctStyle(displayPlayheadPct)} />
          </div>
        </section>
      </div>

      <div className="rp-event-body" ref={eventBodyRef}>
        <h2 className="rp-event-list-title">
          {t(mode === 'cloud' ? 'recPlayback.logbookRows' : 'recPlayback.detectedEvents', {
            count: visibleRows.length,
            defaultValue: mode === 'cloud' ? '事件與操作 ({{count}})' : '偵測到事件 ({{count}})',
          })}
        </h2>
        {unreachable && (
          <p className="rp-error" role="alert">
            {t('recPlayback.labelUnknownSdcard')} <button className="btn-ghost" onClick={reload}>{t('common.retry')}</button>
          </p>
        )}
        {loading && rows.length === 0 && <p className="rp-status">{t('recPlayback.loading')}</p>}
        {!loading && visibleRows.length === 0 && !unreachable && (
          <p className="rp-status">
            {t(mode === 'cloud' ? 'recPlayback.emptyLogbook' : 'recPlayback.empty', {
              defaultValue: mode === 'cloud' ? '此範圍內沒有事件與操作。' : '此範圍內沒有事件。',
            })}
          </p>
        )}

        <div className="rp-event-table" role="list">
          {visibleRows.map((row) => {
            const { deviceId, ev } = row;
            const isCommand = isCommandRow(row);
            const cluster = clusterOfEvent({ event_id: ev.event_id_hex ?? '', event_type: '' });
            const isCloud = ev.record_location === 'cloud';
            const playable = !isCommand && (isCloud || !!deviceHlsBase?.(deviceId) || remoteEnabled);
            const key = eventKeyOf(deviceId, ev);
            const storage = rowStorageKind(ev);
            return (
              <div
                key={key}
                ref={(node) => {
                  if (node) rowRefs.current.set(key, node);
                  else rowRefs.current.delete(key);
                }}
                className={`rp-event-row rp-event-row--${storage} ${key === activeEventKey ? 'is-active' : ''}`}
                role="listitem"
              >
                <button
                  type="button"
                  className="rp-event-select"
                  disabled={!playable}
                  onClick={() => playRow(deviceId, ev)}
                  aria-label={`${isCommand ? t('recPlayback.commandAudit') : cluster.label} ${formatTimestampUs(ev.event_ts_us)}`}
                >
                  {/* 縮圖:has_snapshot 事件拉 per-event 真縮圖(RpEventThumb,三層快取),
                      無圖 / 載入中 / 404 退回既有中性影片 glyph placeholder(不隨事件型別變,對齊 native)。
                      事件型別分類(人/動物…)由右側 rp-event-icon(cluster.icon)呈現,不在縮圖上重複。
                      command 列無縮圖,保留原 duration badge。 */}
                  <span className={`rp-event-thumb rp-event-thumb--${storage}`} aria-hidden>
                    {isCommand
                      ? <span className="rp-thumb-duration">{t('recPlayback.commandBadge')}</span>
                      : ev.has_snapshot
                        ? <RpEventThumb deviceId={deviceId} ev={ev} />
                        : <span className="rp-thumb-glyph" />}
                  </span>
                  <span className="rp-event-main">
                    <span className="rp-event-time">{formatClock(ev.event_ts_us, locale, true, camZone(deviceId) ?? viewZone)}</span>
                    <span className="rp-event-detail">
                      <span className={`rp-event-icon rp-event-icon--${isCommand ? 'other' : cluster.key}`} aria-hidden>{isCommand ? '⚙' : cluster.icon}</span>
                      <span className="rp-event-type">{isCommand ? row.meta?.commandLabel ?? row.meta?.action ?? t('recPlayback.commandAudit') : rowSubtitle(cluster.label, row)}</span>
                    </span>
                    {(() => {
                      const ai = isCommand ? null : eventAiLine(t, row.meta);
                      return ai ? (
                        <span className={`rp-event-ai${ai.confirmed ? ' rp-event-ai--confirmed' : ''}`} data-testid="event-ai">
                          {ai.text}
                        </span>
                      ) : null;
                    })()}
                    {devices.length > 1 && <span className="rp-event-device">{deviceId}</span>}
                  </span>
                </button>
                {/* 死的 ⋮ 裝飾換成可用的下載鈕(對齊 iOS/Android)。下載的是事件實際片段(與播放同源)
                    → transmux 成 progressive MP4 → 存檔。雲端走 S3 clip;SD 走 device VOD tunnel。 */}
                {(() => {
                  const canDl = !isCommand && (isCloud || remoteEnabled || !!deviceHlsBase?.(deviceId));
                  if (!canDl) return <span className="rp-event-dots" aria-hidden>⋮</span>;
                  return (
                    <button
                      type="button"
                      className="rp-event-download"
                      disabled={downloadingKey !== null}
                      onClick={() => void (isCloud ? downloadEvent(deviceId, ev) : downloadSdEvent(deviceId, ev))}
                      aria-label={t('recPlayback.download', { defaultValue: '下載' })}
                      title={t('recPlayback.download', { defaultValue: '下載' })}
                    >
                      {downloadingKey === key ? <span className="rp-spinner" aria-hidden /> : '⬇'}
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>
        {/* Infinite-scroll sentinel: intersecting it (while hasMore) pulls the next page. */}
        <div ref={sentinelRef} />
        {hasMore && rows.length > 0 && (
          <button
            type="button"
            className="btn-ghost rp-load-more"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? t('common.loading') : t('common.loadMore')}
          </button>
        )}
      </div>
    </div>
  );
}
